/**
 * Živá (~1 Hz) hodnota pro device topic z RYCHLÉHO WS kanálu (usePip/Tracer/BmsStore.telemetry),
 * místo pomalého 15s MQTT (useTopic). Self-contained: parsuje `zige/(pip|tracer|bms)-<slug>/<suffix>/val`
 * a vybírá konkrétní pole přes SDÍLENOU topic-mapu (@globals/telemetry) — stejný zdroj pravdy jako server publish.
 *
 *  - Vrací `undefined` když to není živý číselný device topic / chybí telemetrie → volající spadne na useTopic.
 *  - `stale` = telemetrie starší než LIVE_STALE_MS (zařízení offline) → volající fallbackuje na useTopic
 *    (kde se uplatní `suspicious` přeškrtnutí). Tracer statistiky jdou pomalejším `tracer:state` (bez timestamp) → never stale.
 *  - Selektory vrací REFERENCI telemetry objektu (stabilní pattern jako usePipDevice) → žádná smyčka;
 *    skalár + staleness se počítají v useMemo. `tick` (500ms) zajistí přepnutí na stale i když stream ustane.
 */

import { useMemo } from "react";
import { useTicker } from "@client/hooks/useTicker";
import { usePipStore } from "@client/store/pip";
import { useTracerStore } from "@client/store/tracer";
import { useBmsStore } from "@client/store/bms";
import {
  BMS_CELL_SUFFIX_RE,
  BMS_TOPIC_MAP,
  bmsCellValue,
  type MetricValue,
  PIP_TOPIC_MAP,
  TRACER_STATS_MAP,
  TRACER_STATS_SUFFIXES,
  TRACER_TOPIC_MAP,
} from "@globals/telemetry";

export interface DeviceLive {
  value: number;
  stale: boolean;
}

const LIVE_STALE_MS = 10_000;
const TOPIC_RE = /^zige\/(pip|tracer|bms)-(.+)\/([^/]+)\/val$/;

type Kind = "pip" | "tracer" | "bms";
interface Parsed {
  kind: Kind;
  slug: string;
  suffix: string;
}

const parse = (topicId: string): Parsed | null => {
  const m = TOPIC_RE.exec(topicId);
  return m ? { kind: m[1] as Kind, slug: m[2], suffix: m[3] } : null;
};

export const useDeviceTopicLive = (topicId: string): DeviceLive | undefined => {
  const parsed = useMemo(() => parse(topicId), [topicId]);
  const kind = parsed?.kind;
  const slug = parsed?.slug;
  const isStat = !!parsed && kind === "tracer" && TRACER_STATS_SUFFIXES.has(parsed.suffix);

  const pipTel = usePipStore((s) => (kind === "pip" && slug ? s.telemetry.get(slug) : undefined));
  const tracerTel = useTracerStore((s) => (kind === "tracer" && slug && !isStat ? s.telemetry.get(slug) : undefined));
  const bmsTel = useBmsStore((s) => (kind === "bms" && slug ? s.telemetry.get(slug) : undefined));
  const tracerStats = useTracerStore((s) =>
    isStat && slug ? (s.devices.find((d) => d.slug === slug)?.runtime?.statistics ?? undefined) : undefined,
  );
  const tick = useTicker();

  return useMemo(() => {
    if (!parsed) return undefined;
    const { kind, suffix } = parsed;

    // Tracer statistiky (runtime.statistics) — bez timestamp → never stale; jen když máme číslo.
    if (isStat) {
      const v = tracerStats ? TRACER_STATS_MAP[suffix]?.get(tracerStats) : undefined;
      return typeof v === "number" ? { value: v, stale: false } : undefined;
    }

    let v: MetricValue | undefined;
    let tsMs: number;
    if (kind === "pip") {
      if (!pipTel) return undefined;
      v = PIP_TOPIC_MAP[suffix]?.get(pipTel);
      tsMs = pipTel.timestamp;
    } else if (kind === "tracer") {
      if (!tracerTel) return undefined;
      v = TRACER_TOPIC_MAP[suffix]?.get(tracerTel);
      tsMs = tracerTel.timestamp;
    } else {
      if (!bmsTel) return undefined;
      const cell = BMS_CELL_SUFFIX_RE.exec(suffix);
      v = cell ? bmsCellValue(bmsTel, Number(cell[1])) : BMS_TOPIC_MAP[suffix]?.get(bmsTel);
      tsMs = bmsTel.timestamp;
    }
    if (typeof v !== "number") return undefined; // string/bool/unknown/null → pomalá cesta (formatMetric popisky)

    // eslint-disable-next-line react-hooks/purity -- useMemo se přepočítá jen při změně telemetrie/tick
    const stale = Date.now() - tsMs > LIVE_STALE_MS;
    return { value: v, stale };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick je záměrná závislost (přepnutí na stale po výpadku)
  }, [parsed, isStat, pipTel, tracerTel, bmsTel, tracerStats, tick]);
};
